import type { WalletAccountsListResult } from "@/modules/wallet/domain/repo/accounts";
import type { WalletAccountsStats } from "@/modules/wallet/domain/repo/accounts-stats";
import type { WalletOverview } from "@/modules/wallet/domain/repo/overview";
import type { WalletTransactionsListResult } from "@/modules/wallet/domain/repo/transactions";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type {
  WalletAccountsListResult,
  WalletAccountsStats,
  WalletOverview,
  WalletTransactionsListResult,
};
export type WalletAccountItem = WalletAccountsListResult["data"][number];
export type WalletTransactionItem =
  WalletTransactionsListResult["data"][number];

export type TransactionsQuery = OffsetPageQueryDTO & {
  walletId?: string;
  search?: string;
};

export const walletApi = {
  async getOverview(): Promise<WalletOverview> {
    return fetcher.get<WalletOverview>(`${config.apiUrl}/wallet/overview`);
  },

  async getAccountsStats(): Promise<WalletAccountsStats> {
    return fetcher.get<WalletAccountsStats>(
      `${config.apiUrl}/wallet/accounts/stats`,
    );
  },

  async listAccounts(
    query: OffsetPageQueryDTO,
  ): Promise<WalletAccountsListResult> {
    const url = new URL(`${config.apiUrl}/wallet/accounts`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<WalletAccountsListResult>(url.toString());
  },

  async listTransactions(
    query: TransactionsQuery,
  ): Promise<WalletTransactionsListResult> {
    const url = new URL(`${config.apiUrl}/wallet/transactions`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    if (query.walletId) url.searchParams.set("walletId", query.walletId);
    if (query.search) url.searchParams.set("search", query.search);
    return fetcher.get<WalletTransactionsListResult>(url.toString());
  },

  async updateKycTier(
    walletId: string,
    kycTier: string,
  ): Promise<{ ok: boolean }> {
    return fetcher.put<{ ok: boolean }>(
      `${config.apiUrl}/wallet/accounts/${walletId}/kyc-tier`,
      { kycTier },
    );
  },
};
